import { SHAKER_METAFIELD_KEY } from '@portal/api/constants';
import {
  SHOPIFY_5CT_TRAVEL_PACK_VARIANT_ID_V53,
  SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53,
  SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53,
  SHOPIFY_AG1_POUCH_OTP_VARIANT_ID_V53,
  SHOPIFY_AG1_SCOOP_VARIANT_V53,
  SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID,
  SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53,
  SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53,
  SHOPIFY_TRAVEL_PACK_OTP_VARIANT_ID_V53,
  SHOPIFY_OMEGA_SUB_BUNDLE_VARIANT_ID,
  SHOPIFY_AGZ_TRAVEL_PACK_DISCOUNTED_SUB_VARIANT_ID,
  SHOPIFY_AGZ_TRAVEL_PACK_OTP_VARIANT_ID
} from '@portal/config/base';
import { STORE_SPECIFIC_UNIQUE_VARIANTS_MAP } from '@portal/constants/products';
import { CollectionProduct } from 'api/shopifyV2/types';
import { FLAVOR_CONFIG, ProductFlavor, getFlavorConfigFromVariantId } from 'constants/flavors';
import { AlternateFlavor } from 'processes/ShopV2/ShopV2.types';
import { StoreCode } from 'schema/storeCode';
import { getFormattedShopifyId, getIdFromShopifyFormattedId } from 'shared/utils/shopify/ids';
import { SubscriptionLineItemV2 } from 'state/internal/getSubscriptionDetails/types';
import { ProductType, SubscriptionType } from './types';

interface FlavorGroup {
  baseProduct: CollectionProduct;
  variants: AlternateFlavor[];
}

export interface GroupedItems {
  [key: string]: FlavorGroup;
}

interface VariantIdObject {
  original: string;
  tropical: string;
  berry: string;
  citrus: string;
}

// Helper functions to reduce duplication
export const normalizeVariantId = (variantId: string): string => {
  if (variantId.startsWith('gid://shopify/')) {
    // Recursively extract the ID from the nested prefix
    return normalizeVariantId(getIdFromShopifyFormattedId(variantId));
  }

  return variantId;
};

const getVariantPrice = (variant: { price: { amount: string } }): number => {
  return Number(variant.price.amount);
};

export const normalizeProductTitle = (title: string) => {
  return title.split('-')[0].trim();
};

/**
 * Gets all V53 product variant IDs for a given store.
 * This is the single source of truth for all variant IDs.
 * @param store The store code
 * @returns Object containing all variant information with both original and tropical flavors
 */
export const getV53Variants = (store: StoreCode) => {
  return {
    otpPouch: {
      original: SHOPIFY_AG1_POUCH_OTP_VARIANT_ID_V53[store].default,
      tropical: SHOPIFY_AG1_POUCH_OTP_VARIANT_ID_V53[store].tropical || '',
      citrus: SHOPIFY_AG1_POUCH_OTP_VARIANT_ID_V53[store].citrus || '',
      berry: SHOPIFY_AG1_POUCH_OTP_VARIANT_ID_V53[store].berry || ''
    },
    singlePouch: {
      original: SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53[store].default,
      tropical: SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53[store].tropical || '',
      citrus: SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53[store].citrus || '',
      berry: SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53[store].berry || ''
    },
    doublePouch: {
      original: SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53[store].default,
      tropical: SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53[store].tropical || '',
      citrus: SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53[store].citrus || '',
      berry: SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53[store].berry || ''
    },
    otpPack: {
      original: SHOPIFY_TRAVEL_PACK_OTP_VARIANT_ID_V53[store].default,
      tropical: SHOPIFY_TRAVEL_PACK_OTP_VARIANT_ID_V53[store].tropical || '',
      citrus: SHOPIFY_TRAVEL_PACK_OTP_VARIANT_ID_V53[store].citrus || '',
      berry: SHOPIFY_TRAVEL_PACK_OTP_VARIANT_ID_V53[store].berry || ''
    },
    singlePack: {
      original: SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53[store].default,
      tropical: SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53[store].tropical || '',
      citrus: SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53[store].citrus || '',
      berry: SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53[store].berry || ''
    },
    doublePack: {
      original: SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53[store].default,
      tropical: SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53[store].tropical || '',
      citrus: SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53[store].citrus || '',
      berry: SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53[store].berry || ''
    },
    travelPack5ct: {
      original: SHOPIFY_5CT_TRAVEL_PACK_VARIANT_ID_V53[store].default,
      tropical: SHOPIFY_5CT_TRAVEL_PACK_VARIANT_ID_V53[store].tropical || ''
    }
  };
};

/**
 * Gets all NOT OTP ONLY AGZ product variant IDs for a given store.
 * @param store The store code
 * @returns Object containing all NOT OTP ONLY AGZ variant information with different flavors
 */
export const getAgzVariants = (store: StoreCode) => {
  return {
    singlePack: {
      chocolate: [SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID[store].chocolate || ''],
      chocolateMint: [SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID[store].chocolateMint || ''],
      mixedBerry: [SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID[store].mixedBerry || '']
    },
    discountedPack: {
      chocolate: [SHOPIFY_AGZ_TRAVEL_PACK_DISCOUNTED_SUB_VARIANT_ID[store].chocolate || ''],
      chocolateMint: [SHOPIFY_AGZ_TRAVEL_PACK_DISCOUNTED_SUB_VARIANT_ID[store].chocolateMint || ''],
      mixedBerry: [SHOPIFY_AGZ_TRAVEL_PACK_DISCOUNTED_SUB_VARIANT_ID[store].mixedBerry || '']
    },
    otpPack: {
      chocolate: [SHOPIFY_AGZ_TRAVEL_PACK_OTP_VARIANT_ID[store].chocolate || ''],
      chocolateMint: [SHOPIFY_AGZ_TRAVEL_PACK_OTP_VARIANT_ID[store].chocolateMint || ''],
      mixedBerry: [SHOPIFY_AGZ_TRAVEL_PACK_OTP_VARIANT_ID[store].mixedBerry || '']
    }
  };
};

/**
 * Gets all AGZ product variant IDs for a given store.
 * This is the single source of truth for all AGZ variant IDs.
 * @param store The store code
 * @returns Object containing all AGZ variant information with different flavors
 */
export const getAgzVariantsWithOtp = (store: StoreCode) => {
  return {
    singlePack: {
      chocolate: [
        SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID[store].chocolate || '',
        SHOPIFY_AGZ_TRAVEL_PACK_DISCOUNTED_SUB_VARIANT_ID[store].chocolate || '',
        SHOPIFY_AGZ_TRAVEL_PACK_OTP_VARIANT_ID[store].chocolate || ''
      ],
      chocolateMint: [
        SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID[store].chocolateMint || '',
        SHOPIFY_AGZ_TRAVEL_PACK_DISCOUNTED_SUB_VARIANT_ID[store].chocolateMint || '',
        SHOPIFY_AGZ_TRAVEL_PACK_OTP_VARIANT_ID[store].chocolateMint || ''
      ],
      mixedBerry: [
        SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID[store].mixedBerry || '',
        SHOPIFY_AGZ_TRAVEL_PACK_DISCOUNTED_SUB_VARIANT_ID[store].mixedBerry || '',
        SHOPIFY_AGZ_TRAVEL_PACK_OTP_VARIANT_ID[store].mixedBerry || ''
      ]
    }
  };
};

/**
 * Gets an array of AGZ flavors mathcing the flavor of the received variant ID
 * @param variantId The variant ID to check
 * @param store The store code
 * @returns Array containing AGZ variant IDs with the same flavor
 */
export const getAgzFlavorsWithGate = (variantId: string, store: StoreCode) => {
  const {
    singlePack: { chocolate, chocolateMint, mixedBerry }
  } = getAgzVariantsWithOtp(store);
  if (chocolate.includes(variantId)) return chocolate;
  if (chocolateMint.includes(variantId)) return chocolateMint;
  if (mixedBerry.includes(variantId)) return mixedBerry;

  return undefined;
};

/**
 * Gets Grandfathered AGZ product variant IDs for a given store.
 * @param store The store code
 * @returns Array containing Grandfathered AGZ variant IDs from all flavors
 */
export const getGrandfatheredAgzVariants = (store: StoreCode) => {
  return [
    SHOPIFY_AGZ_TRAVEL_PACK_DISCOUNTED_SUB_VARIANT_ID[store].chocolate || '',
    SHOPIFY_AGZ_TRAVEL_PACK_DISCOUNTED_SUB_VARIANT_ID[store].chocolateMint || '',
    SHOPIFY_AGZ_TRAVEL_PACK_DISCOUNTED_SUB_VARIANT_ID[store].mixedBerry || ''
  ];
};

/**
 * Determines if the received variant ID is a None Grandfathered AGZ variant
 * @param store The store code
 * @returns Object containing all AGZ variant information with different flavors
 */
export const isGrandFatherAGZ = (targetVariantId: string, store: StoreCode) => {
  const allGrandFatherVariants = getGrandfatheredAgzVariants(store);

  return allGrandFatherVariants.includes(targetVariantId);
};

export const swapAGZPricing = (variantId: string, isOTP: boolean, store: StoreCode) => {
  const { singlePack: agzSinglePack } = getAgzVariantsWithOtp(store);
  const isGrandFather = isGrandFatherAGZ(variantId, store);

  if (agzSinglePack.chocolate.includes(variantId)) {
    if (isGrandFather) {
      return isOTP
        ? SHOPIFY_AGZ_TRAVEL_PACK_OTP_VARIANT_ID[store].chocolate
        : SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID[store].chocolate;
    }
    return SHOPIFY_AGZ_TRAVEL_PACK_DISCOUNTED_SUB_VARIANT_ID[store].chocolate;
  }

  if (agzSinglePack.chocolateMint.includes(variantId)) {
    if (isGrandFather) {
      return isOTP
        ? SHOPIFY_AGZ_TRAVEL_PACK_OTP_VARIANT_ID[store].chocolateMint
        : SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID[store].chocolateMint;
    }
    return SHOPIFY_AGZ_TRAVEL_PACK_DISCOUNTED_SUB_VARIANT_ID[store].chocolateMint;
  }

  if (agzSinglePack.mixedBerry.includes(variantId)) {
    if (isGrandFather) {
      return isOTP
        ? SHOPIFY_AGZ_TRAVEL_PACK_OTP_VARIANT_ID[store].mixedBerry
        : SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID[store].mixedBerry;
    }
    return SHOPIFY_AGZ_TRAVEL_PACK_DISCOUNTED_SUB_VARIANT_ID[store].mixedBerry;
  }

  return undefined;
};

const mapVariants = (pouchVariants: VariantIdObject, travelPackVariants: VariantIdObject) => {
  return {
    [pouchVariants.original]: travelPackVariants.original,
    [pouchVariants.tropical]: travelPackVariants.tropical,
    [pouchVariants.berry]: travelPackVariants.berry,
    [pouchVariants.citrus]: travelPackVariants.citrus,
    [travelPackVariants.original]: pouchVariants.original,
    [travelPackVariants.tropical]: pouchVariants.tropical,
    [travelPackVariants.berry]: pouchVariants.berry,
    [travelPackVariants.citrus]: pouchVariants.citrus
  };
};

/**
 * Creates a mapping between pouch and travel pack variants.
 * Used internally by switchProductFormat to find matching variants.
 * @param store The store code for variant mapping
 * @returns A mapping object where each key is a variant ID and its value is the corresponding format's variant ID
 */
const getProductVariantMappings = (store: StoreCode) => {
  const { singlePouch, singlePack, doublePouch, doublePack } = getV53Variants(store);

  return {
    ...mapVariants(singlePouch, singlePack),
    ...mapVariants(doublePouch, doublePack)
  };
};

/**
 * Generic function to check if a variant ID belongs to a specific product type
 * @param variantId Variant ID to check
 * @param type Product type to check ('pouch', 'travelPack', or 'agz')
 * @param store Store code
 * @returns boolean indicating if the variant matches the specified type
 */
export const isVariantOfType = (
  variantId: string,
  type: ProductType,
  store: StoreCode
): boolean => {
  if (!variantId) return false;

  const normalizedId = normalizeVariantId(variantId);

  if (type === 'pouch') {
    const { singlePouch, doublePouch } = getV53Variants(store);
    return (
      normalizedId === singlePouch.original ||
      normalizedId === singlePouch.tropical ||
      normalizedId === singlePouch.berry ||
      normalizedId === singlePouch.citrus ||
      normalizedId === doublePouch.original ||
      normalizedId === doublePouch.tropical ||
      normalizedId === doublePouch.berry ||
      normalizedId === doublePouch.citrus
    );
  }

  if (type === 'pouchOTP') {
    const { otpPouch } = getV53Variants(store);
    return normalizedId === otpPouch.original || normalizedId === otpPouch.tropical;
  }

  if (type === 'travelPack') {
    const { singlePack, doublePack, travelPack5ct } = getV53Variants(store);
    return (
      normalizedId === singlePack.original ||
      normalizedId === singlePack.tropical ||
      normalizedId === singlePack.berry ||
      normalizedId === singlePack.citrus ||
      normalizedId === doublePack.original ||
      normalizedId === doublePack.tropical ||
      normalizedId === doublePack.berry ||
      normalizedId === doublePack.citrus ||
      normalizedId === travelPack5ct.original ||
      normalizedId === travelPack5ct.tropical
    );
  }

  if (type === 'travelPackOTP') {
    const { otpPack } = getV53Variants(store);
    return normalizedId === otpPack.original || normalizedId === otpPack.tropical;
  }

  if (type === 'agz') {
    const {
      singlePack: agzSinglePack,
      discountedPack: agzDiscountedPack,
      otpPack: agzOtpPack
    } = getAgzVariants(store);

    return (
      agzSinglePack.chocolate.includes(normalizedId) ||
      agzSinglePack.chocolateMint.includes(normalizedId) ||
      agzSinglePack.mixedBerry.includes(normalizedId) ||
      agzDiscountedPack.chocolate.includes(normalizedId) ||
      agzDiscountedPack.chocolateMint.includes(normalizedId) ||
      agzDiscountedPack.mixedBerry.includes(normalizedId) ||
      agzOtpPack.chocolate.includes(normalizedId) ||
      agzOtpPack.chocolateMint.includes(normalizedId) ||
      agzOtpPack.mixedBerry.includes(normalizedId)
    );
  }

  return false;
};

/**
 * Determines the product type based on the variant ID
 */
export const getProductType = (variantId: string, store: StoreCode): ProductType | undefined => {
  if (isVariantOfType(variantId, 'pouch', store)) {
    return 'pouch';
  }
  if (isVariantOfType(variantId, 'travelPack', store)) {
    return 'travelPack';
  }
  if (isVariantOfType(variantId, 'agz', store)) {
    return 'agz';
  }
  return undefined;
};

/**
 * Checks if a variant ID matches any of the V53 variants
 */
export const isV53Product = (variantId: string, store: StoreCode): boolean => {
  return (
    isVariantOfType(variantId, 'pouch', store) || isVariantOfType(variantId, 'travelPack', store)
  );
};

/**
 * Checks if a variant ID matches any of the V53 variants for OTP
 */
export const isV53ProductOTP = (variantId: string, store: StoreCode): boolean => {
  return (
    isVariantOfType(variantId, 'pouchOTP', store) ||
    isVariantOfType(variantId, 'travelPackOTP', store)
  );
};

/**
 * Checks if a variant ID matches any of the AGZ variants
 */
export const isAgzProduct = (variantId: string, store: StoreCode): boolean => {
  return isVariantOfType(variantId, 'agz', store);
};

/**
 * Checks if two variants are of the same product type
 */
export const isSameProductType = (
  variantId1: string,
  variantId2: string,
  store: StoreCode
): boolean => {
  const type1 = getProductType(variantId1, store);
  const type2 = getProductType(variantId2, store);
  return type1 !== undefined && type1 === type2;
};

/**
 * Checks if a variant ID corresponds to a required subscription product (V53 or AGZ).
 * Required products are AG1 pouches/packs (single/double, original/tropical) and AGZ single packs.
 * Excludes V53 5ct travel packs.
 * @param variantId The variant ID to check
 * @param store The store code
 * @returns True if the variant ID is a required subscription product, false otherwise
 */
export const isRequiredSubscriptionProduct = (variantId: string, store: StoreCode) => {
  if (!variantId) return false;

  const normalizedId = normalizeVariantId(variantId);

  // Check V53 required products (pouches and regular travel packs)
  const v53Variants = getV53Variants(store);
  const isRequiredV53 =
    normalizedId === v53Variants.singlePouch.original ||
    normalizedId === v53Variants.singlePouch.tropical ||
    normalizedId === v53Variants.singlePouch.citrus ||
    normalizedId === v53Variants.singlePouch.berry ||
    normalizedId === v53Variants.doublePouch.original ||
    normalizedId === v53Variants.doublePouch.tropical ||
    normalizedId === v53Variants.doublePouch.citrus ||
    normalizedId === v53Variants.doublePouch.berry ||
    normalizedId === v53Variants.singlePack.original ||
    normalizedId === v53Variants.singlePack.tropical ||
    normalizedId === v53Variants.singlePack.citrus ||
    normalizedId === v53Variants.singlePack.berry ||
    normalizedId === v53Variants.doublePack.original ||
    normalizedId === v53Variants.doublePack.tropical ||
    normalizedId === v53Variants.doublePack.citrus ||
    normalizedId === v53Variants.doublePack.berry;

  if (isRequiredV53) {
    return true;
  }

  // Check AGZ required products (single pack, all flavors)
  const agzVariants = getAgzVariants(store);
  const isRequiredAgz =
    agzVariants.singlePack.chocolate.includes(normalizedId) ||
    agzVariants.singlePack.chocolateMint.includes(normalizedId) ||
    agzVariants.singlePack.mixedBerry.includes(normalizedId) ||
    agzVariants.discountedPack.chocolate.includes(normalizedId) ||
    agzVariants.discountedPack.chocolateMint.includes(normalizedId) ||
    agzVariants.discountedPack.mixedBerry.includes(normalizedId);

  return isRequiredAgz;
};

/**
 * Maps between single and double variant IDs.
 * @param id The variant ID to map
 * @param store The store code
 * @param direction The mapping direction: 'toSingle' or 'toDouble'
 * @returns The mapped ID, or the original ID if no mapping exists
 */
export const mapVariantSize = (
  variantId: string,
  store: StoreCode,
  direction: 'toSingle' | 'toDouble'
): string => {
  const { singlePouch, singlePack, doublePouch, doublePack } = getV53Variants(store);
  const normalizedId = normalizeVariantId(variantId);
  const isFullShopifyId = variantId.startsWith('gid://shopify/');

  let mappedId: string | undefined;

  if (direction === 'toSingle') {
    if (normalizedId === doublePouch.original) {
      mappedId = singlePouch.original;
    }
    if (normalizedId === doublePouch.tropical) {
      mappedId = singlePouch.tropical;
    }
    if (normalizedId === doublePouch.berry) {
      mappedId = singlePouch.berry;
    }
    if (normalizedId === doublePouch.citrus) {
      mappedId = singlePouch.citrus;
    }
    if (normalizedId === doublePack.original) {
      mappedId = singlePack.original;
    }
    if (normalizedId === doublePack.tropical) {
      mappedId = singlePack.tropical;
    }
    if (normalizedId === doublePack.berry) {
      mappedId = singlePack.berry;
    }
    if (normalizedId === doublePack.citrus) {
      mappedId = singlePack.citrus;
    }
  } else {
    if (normalizedId === singlePouch.original) {
      mappedId = doublePouch.original;
    }
    if (normalizedId === singlePouch.tropical) {
      mappedId = doublePouch.tropical;
    }
    if (normalizedId === singlePouch.berry) {
      mappedId = doublePouch.berry;
    }
    if (normalizedId === singlePouch.citrus) {
      mappedId = doublePouch.citrus;
    }
    if (normalizedId === singlePack.original) {
      mappedId = doublePack.original;
    }
    if (normalizedId === singlePack.tropical) {
      mappedId = doublePack.tropical;
    }
    if (normalizedId === singlePack.berry) {
      mappedId = doublePack.berry;
    }
    if (normalizedId === singlePack.citrus) {
      mappedId = doublePack.citrus;
    }
  }

  // If we found a mapping, convert it to the same format as the input
  if (mappedId && isFullShopifyId) {
    return getFormattedShopifyId('ProductVariant', mappedId);
  }

  // Return the mapped ID (in normalized form) or the original ID if no mapping
  return mappedId || variantId;
};

/**
 * Maps double variant IDs to single variant IDs
 * Kept for backward compatibility
 */
export const mapDoubleIdsToSingle = (id: string, store: StoreCode): string => {
  return mapVariantSize(id, store, 'toSingle');
};

/**
 * Maps single variant IDs to double variant IDs
 * Kept for backward compatibility
 */
export const mapSingleIdsToDouble = (id: string, store: StoreCode): string => {
  return mapVariantSize(id, store, 'toDouble');
};

export const mapRelatedItems = (variantId: string, store: StoreCode) => {
  const shortId = normalizeVariantId(variantId);
  const { singlePouch, singlePack, doublePouch, doublePack } = getV53Variants(store);

  const v53IdsMapper = {
    [singlePouch.original]: [singlePouch.tropical, doublePouch.tropical],
    [doublePouch.original]: [singlePouch.tropical, doublePouch.tropical],
    [singlePouch.tropical ?? '']: [singlePouch.original, doublePouch.original],
    [doublePouch.tropical ?? '']: [singlePouch.original, doublePouch.original],
    [singlePouch.berry ?? '']: [singlePouch.original, doublePouch.original],
    [doublePouch.berry ?? '']: [singlePouch.original, doublePouch.original],
    [singlePouch.citrus ?? '']: [singlePouch.original, doublePouch.original],
    [doublePouch.citrus ?? '']: [singlePouch.original, doublePouch.original],
    [singlePack.original]: [singlePack.tropical, doublePack.tropical],
    [doublePack.original]: [singlePack.tropical, doublePack.tropical],
    [singlePack.tropical ?? '']: [singlePack.original, doublePack.original],
    [doublePack.tropical ?? '']: [singlePack.original, doublePack.original],
    [singlePack.berry ?? '']: [singlePack.original, doublePack.original],
    [doublePack.berry ?? '']: [singlePack.original, doublePack.original],
    [singlePack.citrus ?? '']: [singlePack.original, doublePack.original],
    [doublePack.citrus ?? '']: [singlePack.original, doublePack.original]
  };

  return v53IdsMapper[shortId];
};

/**
 * Converts line items between pouch and travel pack formats while maintaining flavor and quantity.
 * For example, converts a single pouch subscription to a single travel pack subscription.
 *
 * @param lineItems The line items to convert
 * @param store The store code for variant mapping
 * @param isNextOrderOnly Whether to mark converted items as one-time purchases
 * @returns Converted line items with the opposite format (pouch ↔ travel pack)
 */
export const switchProductFormat = (
  lineItems: SubscriptionLineItemV2[],
  store: StoreCode,
  isNextOrderOnly: boolean
): SubscriptionLineItemV2[] => {
  // Early return if we have no items
  if (!lineItems.length) return lineItems;

  const variantMappings = getProductVariantMappings(store);
  const updatedItems: SubscriptionLineItemV2[] = [];

  // Process each line item
  lineItems.forEach((item) => {
    // Skip one-time products
    if (item.is_one_time) {
      updatedItems.push(item);
      return;
    }

    const variantId = normalizeVariantId(item.product.shopify_variant_id);
    const targetVariantId =
      variantMappings[variantId] || variantMappings[item.product.shopify_variant_id];

    // If we found a corresponding format variant, create a new item with it
    if (targetVariantId) {
      // Convert to full Shopify variant ID format
      const newShopifyVariantId = getFormattedShopifyId('ProductVariant', targetVariantId);

      updatedItems.push({
        ...item,
        is_one_time: isNextOrderOnly,
        product: {
          ...item.product,
          shopify_variant_id: newShopifyVariantId
        }
      });
    } else {
      // If it's not a convertible variant, keep it as is
      updatedItems.push(item);
    }
  });

  return updatedItems;
};

/**
 * Generalized function to check if a variant matches a specific product type and flavor
 * @param variantId Variant ID to check
 * @param productType Product type to check against ('pouch', 'travelPack', or 'agz')
 * @param flavor Specific flavor to check for
 * @param store Store code
 * @returns boolean indicating if the variant matches the specified product type and flavor
 */
export const isProductVariantFlavor = (
  variantId: string,
  productType: ProductType,
  flavor: ProductFlavor,
  store: StoreCode
): boolean => {
  if (!variantId) return false;

  const normalizedId = normalizeVariantId(variantId);

  // V53 Products (pouches and travel packs)
  if (productType === 'pouch') {
    const { singlePouch, doublePouch } = getV53Variants(store);

    if (flavor === 'original') {
      return normalizedId === singlePouch.original || normalizedId === doublePouch.original;
    }

    if (flavor === 'tropical') {
      return normalizedId === singlePouch.tropical || normalizedId === doublePouch.tropical;
    }
  }

  if (productType === 'travelPack') {
    const { singlePack, doublePack, travelPack5ct } = getV53Variants(store);

    if (flavor === 'original') {
      return (
        normalizedId === singlePack.original ||
        normalizedId === doublePack.original ||
        normalizedId === travelPack5ct.original
      );
    }

    if (flavor === 'tropical') {
      return (
        normalizedId === singlePack.tropical ||
        normalizedId === doublePack.tropical ||
        normalizedId === travelPack5ct.tropical
      );
    }
  }

  // AGZ Products
  if (productType === 'agz') {
    const {
      singlePack: agzSinglePack,
      discountedPack: agzDiscountedPack,
      otpPack: agzOtpPack
    } = getAgzVariants(store);

    if (flavor === 'chocolate') {
      return (
        agzSinglePack.chocolate.includes(normalizedId) ||
        agzDiscountedPack.chocolate.includes(normalizedId) ||
        agzOtpPack.chocolate.includes(normalizedId)
      );
    }

    if (flavor === 'chocolateMint') {
      return (
        agzSinglePack.chocolateMint.includes(normalizedId) ||
        agzDiscountedPack.chocolateMint.includes(normalizedId) ||
        agzOtpPack.chocolateMint.includes(normalizedId)
      );
    }

    if (flavor === 'mixedBerry') {
      return (
        agzSinglePack.mixedBerry.includes(normalizedId) ||
        agzDiscountedPack.mixedBerry.includes(normalizedId) ||
        agzOtpPack.mixedBerry.includes(normalizedId)
      );
    }
  }

  return false;
};

/**
 * Checks if a variant ID matches any of the tropical V53 variants
 */
export const isTropicalV53Product = (variantId: string, store: StoreCode): boolean => {
  if (!variantId) return false;

  const normalizedId = normalizeVariantId(variantId);
  const { singlePouch, doublePouch, singlePack, doublePack, travelPack5ct } = getV53Variants(store);

  return (
    normalizedId === singlePouch.tropical ||
    normalizedId === doublePouch.tropical ||
    normalizedId === singlePack.tropical ||
    normalizedId === doublePack.tropical ||
    normalizedId === travelPack5ct.tropical
  );
};

/**
 * Checks if a variant ID matches any of the berry V53 variants
 */
export const isBerryV53Product = (variantId: string, store: StoreCode): boolean => {
  if (!variantId) return false;

  const normalizedId = normalizeVariantId(variantId);
  const { singlePouch, doublePouch, singlePack, doublePack } = getV53Variants(store);

  return (
    normalizedId === singlePouch.berry ||
    normalizedId === doublePouch.berry ||
    normalizedId === singlePack.berry ||
    normalizedId === doublePack.berry
  );
};

/**
 * Checks if a variant ID matches any of the citrus V53 variants
 */
export const isCitrusV53Product = (variantId: string, store: StoreCode): boolean => {
  if (!variantId) return false;

  const normalizedId = normalizeVariantId(variantId);
  const { singlePouch, doublePouch, singlePack, doublePack } = getV53Variants(store);

  return (
    normalizedId === singlePouch.citrus ||
    normalizedId === doublePouch.citrus ||
    normalizedId === singlePack.citrus ||
    normalizedId === doublePack.citrus
  );
};

export const groupProductsWithFlavors = (
  collectionProducts: CollectionProduct[],
  store: StoreCode,
  altFlavorProducts: AlternateFlavor[] = []
): GroupedItems => {
  return collectionProducts.reduce<GroupedItems>((acc, product) => {
    const variantId = product.variants[0].id;

    // Check if it's either a V53 product or an AGZ product
    if (!isV53Product(variantId, store) && !isAgzProduct(variantId, store)) {
      return acc;
    }

    // Get flavor titles from FLAVOR_CONFIG, excluding 'Original'
    const flavorStrings = Object.entries(FLAVOR_CONFIG)
      .filter(([key, config]) => key !== 'default' && config.title !== 'Original') // Exclude default/Original
      .map(([_, config]) => config.title);

    // Clean the title by removing flavor strings and extra spaces
    let cleanedTitle = product.title;
    flavorStrings.forEach((flavor) => {
      // Remove the flavor string, trim potential whitespace around it
      cleanedTitle = cleanedTitle
        .replace(new RegExp(` *${flavor}(?= *(Pouch|Travel Packs|$)?)`, 'gi'), '')
        .trim();
    });

    // Now determine the base product ID from the cleaned title
    const baseProductId = normalizeProductTitle(cleanedTitle);

    const flavorInfo = getFlavorConfigFromVariantId(variantId, store);

    const variantPrice = getVariantPrice(product.variants[0]);

    // Look for matching alt flavor in altFlavorProducts
    const altFlavor = altFlavorProducts.find((alt) => {
      // Get the full variant ID from the alt flavor's variant
      const altFullVariantId = alt.variant?.variants?.[0]?.id;

      return (
        // Match by full variant ID
        altFullVariantId === variantId ||
        // Match by full product ID (this product could be the alternate flavor itself)
        product.id === alt.id ||
        // Match by full originalVariantId (this product could be the original variant)
        product.id === alt.originalVariantId
      );
    });

    // Create flavor variant with default values or from alt flavor
    const flavorVariant: AlternateFlavor = {
      id: variantId,
      title: flavorInfo?.title || product.title,
      color: flavorInfo?.color || '#000000',
      singlePrice: altFlavor?.singlePrice ?? variantPrice,
      multiplePrice: altFlavor?.multiplePrice ?? variantPrice,
      originalPrice: altFlavor?.originalPrice ?? variantPrice,
      variant: product
    };

    // Group variants by their base product
    if (!acc[baseProductId]) {
      acc[baseProductId] = {
        baseProduct: product,
        variants: [flavorVariant]
      };
    } else if (!acc[baseProductId].variants.some((v) => v.id === flavorVariant.id)) {
      acc[baseProductId].variants.push(flavorVariant);
    }

    return acc;
  }, {});
};

export const matchLineItemsToGroups = (
  groupedProducts: GroupedItems,
  modifiedItems: SubscriptionLineItemV2[],
  store: StoreCode
) => {
  return Object.entries(groupedProducts).map(([key, group]) => {
    // Find line items that match any variant in this group
    const groupLineItems = modifiedItems.filter((item) => {
      if (item.is_one_time || item.quantity <= 0) return false;
      const normalizedItemId = mapVariantSize(item.product.shopify_variant_id, store, 'toSingle');
      // Compare normalized item ID against normalized variant IDs in the group
      return group.variants.some((variant) => {
        const normalizedVariantId = mapVariantSize(variant.id, store, 'toSingle');
        return normalizedItemId === normalizedVariantId;
      });
    });

    return {
      key,
      group,
      lineItems: groupLineItems
    };
  });
};

/**
 * Determines if a subscription contains pouches, travel packs, AGZ products, or combinations
 * @param lineItems All line items in the subscription
 * @param store Store code
 * @returns 'pouch', 'travelPack', 'agz', 'both', or undefined if no relevant items
 */
export const determineSubscriptionType = (
  lineItems: SubscriptionLineItemV2[],
  store: StoreCode
): SubscriptionType => {
  // Items that will be active *after* the next order
  const nextSubscriptionItems = lineItems.filter(
    (item) => !item.is_one_time && item.quantity > 0 && item.applied_after_next_order
  );

  // Items currently active in the subscription
  const currentSubscriptionItems = lineItems.filter(
    (item) => !item.is_one_time && item.quantity > 0 && !item.applied_after_next_order
  );

  // Determine which set of items to use
  const itemsToEvaluate =
    nextSubscriptionItems.length > 0 ? nextSubscriptionItems : currentSubscriptionItems;

  // If no relevant items exist in either state, return undefined
  if (itemsToEvaluate.length === 0) {
    return undefined;
  }

  // Check based on the selected items (prioritizing next order state)
  const hasPouchItems = itemsToEvaluate.some((item) => {
    const variantId = item.product.shopify_variant_id;
    return isVariantOfType(variantId, 'pouch', store);
  });

  // Check if there are any travel pack items
  const hasTravelPackItems = itemsToEvaluate.some((item) => {
    const variantId = item.product.shopify_variant_id;
    return isVariantOfType(variantId, 'travelPack', store);
  });

  // Check if there are any AGZ items
  const hasAgzItems = itemsToEvaluate.some((item) => {
    const variantId = item.product.shopify_variant_id;
    return isVariantOfType(variantId, 'agz', store);
  });

  // Return the appropriate type
  if (hasAgzItems) return 'agz'; // Prioritize AGZ if present
  if (hasPouchItems && hasTravelPackItems) return 'both';
  if (hasPouchItems) return 'pouch';
  if (hasTravelPackItems) return 'travelPack';

  return undefined;
};

/**
 * Determines if a subscription is in an empty state.
 * A subscription is considered empty if there are no active required subscription products,
 * or if all required products have been converted to one-time purchases.
 *
 * @param items Line items in the subscription
 * @param store Store code
 * @returns True if the subscription is empty, false otherwise
 */
export const isSubscriptionEmpty = (items: SubscriptionLineItemV2[], store: StoreCode): boolean => {
  // Check if there are any active required subscription products (not one-time)
  const hasRequiredSubscriptionProducts = items.some((item) => {
    if (item.is_one_time || item.quantity <= 0) return false;

    return isRequiredSubscriptionProduct(item.product.shopify_variant_id, store);
  });

  // If we have required subscription products currently active, it's not empty
  if (hasRequiredSubscriptionProducts) {
    return false;
  }

  // Check if there were any subscription products that were switched to next order only
  // (If all required products are now one-time, the subscription is empty)
  const allRequiredProductsAreOneTime = items.every((item) => {
    if (item.quantity <= 0) return true; // Skip items with no quantity

    const isRequired = isRequiredSubscriptionProduct(item.product.shopify_variant_id, store);

    // If it's a required product, it should be subscription (not one-time)
    // If it's one-time, that means it was switched to next order only
    return !isRequired || item.is_one_time;
  });

  // If all required products are one-time OR there are no products at all,
  // the subscription is empty
  return allRequiredProductsAreOneTime;
};

/**
 * Determines if there are no user-added one-time products in the items.
 * Automatically added products like scoop and shaker (V53 products) are not considered.
 * Products with a 'hidden' tag or metafield are also not considered.
 *
 * @param items Line items to check
 * @param store Store code for product type identification
 * @returns True if there are no user-added one-time products, false otherwise
 */
export const isOneTimeProductsEmpty = (
  items: SubscriptionLineItemV2[],
  store: StoreCode
): boolean => {
  // Check if there are any one-time products that are V53 products
  return !items.some((item) => {
    // If it's not a one-time product or has quantity 0, skip it
    if (!item.is_one_time || item.quantity <= 0) return false;
    // Check if product is hidden via metafields
    const isHidden = item.product.metafields?.some(
      (field) =>
        (field.key === 'hidden' && field.value === 'true') ||
        (field.key === 'visibility' && field.value === 'hidden')
    );

    // Only consider V53 products that aren't hidden as user-added one-time products
    return isV53Product(item.product.shopify_variant_id, store) && !isHidden;
  });
};

/**
 * Calculates the correct price per unit for a product based on flavors and quantities
 * @param lineItems Line items for the product
 * @param baseProduct Base product information
 * @param variants Flavor variants array
 * @param store Store code for ID normalization
 * @returns The price per unit
 */
export const calculateProductPrice = (
  lineItems: SubscriptionLineItemV2[],
  baseProduct: CollectionProduct,
  variants: AlternateFlavor[] | undefined,
  store: StoreCode
): number => {
  // Assume lineItems are already filtered for active items
  if (!lineItems.length || !variants?.length) {
    return getVariantPrice(baseProduct.variants[0]);
  }

  const basePrice = getVariantPrice(baseProduct.variants[0]);
  const totalQuantity = lineItems.reduce((total, item) => total + item.quantity, 0);

  // Find active flavor with quantity
  const activeFlavor = variants.find((flavor) => {
    const flavorQuantity = lineItems.reduce((sum, item) => {
      const normalizedItemId = mapVariantSize(item.product.shopify_variant_id, store, 'toSingle');
      const normalizedFlavorId = mapVariantSize(flavor.id, store, 'toSingle');
      // Compare normalized single IDs
      return normalizedItemId === normalizedFlavorId ? sum + item.quantity : sum;
    }, 0);

    return flavorQuantity > 0;
  });

  // If no active flavor, use base price
  if (!activeFlavor) return basePrice;

  // Return price based on quantity tier
  return totalQuantity > 1
    ? activeFlavor.multiplePrice ?? activeFlavor.singlePrice ?? basePrice
    : activeFlavor.singlePrice ?? basePrice;
};

/**
 * Calculates the correct subtotal for all *active* items, taking into account flavor discounts.
 * Excludes items marked as applied_after_next_order.
 * @param items All line items in the subscription
 * @param processedGroups Processed product groups with their line items (potentially including inactive)
 * @param store Store code for variant identification
 * @returns The correct subtotal amount for active items
 */
export const calculateSubtotal = (
  items: SubscriptionLineItemV2[],
  processedGroups: ReturnType<typeof matchLineItemsToGroups>,
  store: StoreCode
): number => {
  // Filter out items that are applied after the next order
  const activeItems = items.filter((item) => !item.applied_after_next_order);

  // Filter processed groups to only include active items and update lineItems within them
  const activeProcessedGroups = processedGroups
    .map((groupData) => {
      const activeGroupLineItems = groupData.lineItems.filter(
        (item) => !item.applied_after_next_order
      );
      return {
        ...groupData,
        lineItems: activeGroupLineItems
      };
    })
    .filter((groupData) => groupData.lineItems.length > 0); // Keep only groups with active items

  // Create a set of IDs that we'll mark as "processed" to avoid double-counting
  const processedItemIds = new Set<number | string>();
  let total = 0;

  // First, calculate correctly priced flavor-based products using only active items
  activeProcessedGroups.forEach(({ group, lineItems }) => {
    // lineItems here are already filtered to be active
    if (!lineItems.length) return;

    // Only special pricing for items with flavors
    if (group.variants?.length) {
      // Use our shared pricing function with active items
      const pricePerUnit = calculateProductPrice(
        lineItems, // These are the active items for this group
        group.baseProduct,
        group.variants,
        store
      );

      // Add to the total and mark these active items as processed
      total += pricePerUnit * lineItems.reduce((sum, item) => sum + item.quantity, 0);
      lineItems.forEach((item) => processedItemIds.add(item.id));
    }
  });

  // Now add any remaining *active* items that weren't in flavor groups
  activeItems.forEach((item) => {
    if (!processedItemIds.has(item.id)) {
      // Add the total_amount of the active item
      total += item.total_amount;
    }
  });

  return total;
};

/**
 * Determines if a product can be added as a subscription item.
 * A product can be a subscription if it has the 'sub' metafield set to 'true',
 * is tagged as 'Core Products', or is a V53 or AGZ product.
 *
 * @param product - Either a LineItemV2 product or a CollectionProduct
 * @param store - The store code for V53 and AGZ product checking
 * @returns boolean - Whether the product can be a subscription
 */
export const canBeSubscription = (
  product: SubscriptionLineItemV2 | CollectionProduct,
  store: StoreCode
): boolean => {
  // Case 1: Check if it's a CollectionProduct (distinguished by 'tags' and 'variants')
  if ('tags' in product && 'variants' in product) {
    if (product.tags.includes('Core Products')) return true;

    const hasSub = product.metafields.some(
      (field) => field && field.key === 'sub' && field.value === 'true'
    );

    return (
      hasSub ||
      (product.variants.length > 0 &&
        (isV53Product(product.variants[0].id, store) ||
          isAgzProduct(product.variants[0].id, store)))
    );
  }

  // Case 2: Check if it's a SubscriptionLineItemV2 (distinguished by having a 'product' property)
  // Note: The 'CollectionProduct' type is handled by the 'tags' in product check above.
  if ('product' in product) {
    const hasSub = product.product.metafields.some(
      (field) => field && field.key === 'sub' && field.value === 'true'
    );

    return (
      hasSub ||
      isV53Product(product.product.shopify_variant_id, store) ||
      isAgzProduct(product.product.shopify_variant_id, store)
    );
  }

  // Fallback if the type doesn't match expected structures
  return false;
};

/**
 * Determines whether a subscription is eligible for a free shaker
 * @param lineItems The line items to check for shaker eligibility
 * @param metafields Customer metafields to check eligibility
 * @param store The store code
 * @returns Boolean indicating if the subscription is eligible for a free shaker
 */
export const checkShakerEligibility = (
  lineItems: SubscriptionLineItemV2[],
  metafields: Array<{ key: string; value: string }> | undefined,
  store: StoreCode
): boolean => {
  // Check if customer previously received a shaker
  const shakerMetafield = metafields?.find((metafield) => metafield.key === SHAKER_METAFIELD_KEY);
  const isMetafieldEligible = !shakerMetafield || shakerMetafield.value === 'false';
  if (!isMetafieldEligible) return false;

  // Check if subscription has eligible products (double sub with quantity > 1) among active items
  const hasEligibleProducts = lineItems.some((item) => {
    // Exclude one-time, inactive (applied after next order), or single quantity items
    if (item.is_one_time || item.applied_after_next_order || item.quantity <= 1) return false;

    // Check if this is a pouch or travel pack product
    const productType = getProductType(item.product.shopify_variant_id, store);
    return (productType === 'pouch' || productType === 'travelPack') && item.quantity > 1;
  });

  return hasEligibleProducts;
};

/**
 * Get the shaker variant ID for the current store
 * @param store The store code
 * @returns The formatted shaker variant ID if available, empty string otherwise
 */
export const getShakerVariantId = (store: StoreCode): string => {
  return STORE_SPECIFIC_UNIQUE_VARIANTS_MAP[store]?.shaker
    ? getFormattedShopifyId(
        'ProductVariant',
        String(STORE_SPECIFIC_UNIQUE_VARIANTS_MAP[store].shaker)
      )
    : '';
};

/**
 * Gets the formatted Shopify variant ID for the AG1 scoop specific to the store.
 * @param store The store code.
 * @returns The formatted scoop variant ID (e.g., 'gid://shopify/ProductVariant/12345').
 */
export const getScoopVariantId = (store: StoreCode): string => {
  const scoopVariantId = SHOPIFY_AG1_SCOOP_VARIANT_V53[store]?.default;

  // If the ID is missing or already formatted, return it as is (or an empty string if missing)
  if (!scoopVariantId || scoopVariantId.startsWith('gid://shopify/')) {
    return scoopVariantId || '';
  }

  // Otherwise, format it
  return getFormattedShopifyId('ProductVariant', scoopVariantId);
};

/**
 * Updates line items to add or remove the `swap_back_to` property,
 * used for the "Next Order Only" product swap feature.
 *
 * @param items The array of subscription line items to update.
 * @param addSwap If true, adds `swap_back_to`. If false, removes it.
 * @param store The current store code.
 * @returns A new array of updated subscription line items.
 */
export const updateSwapBackToFlag = (
  items: SubscriptionLineItemV2[],
  addSwap: boolean,
  store: StoreCode
): SubscriptionLineItemV2[] => {
  return items.map((item) => {
    if (addSwap) {
      // Find original format details to store in swap_back_to
      // We pass false to isNextOrderOnly as we want the *original* item details
      const originalFormatItems = switchProductFormat([item], store, false);
      if (originalFormatItems.length > 0) {
        const originalDetails = originalFormatItems[0];
        // Ensure the swapped item is not the same as the current one
        if (originalDetails.product.shopify_variant_id !== item.product.shopify_variant_id) {
          return {
            ...item,
            swap_back_to: {
              shopify_product_variant_id: originalDetails.product.shopify_variant_id,
              quantity: originalDetails.quantity
            }
          };
        }
      }
      return item;
    }

    // If not addSwap, remove swap_back_to
    const { swap_back_to: _, ...rest } = item;
    return rest;
  });
};

/**
 * Maps products that have discounted prices to the base one
 * (only Omega 3 for now. Expand with AGZ when it's ready)
 */
export const mapDiscountedProducts = (variantId: string, store: StoreCode) => {
  const normalizedId = normalizeVariantId(variantId);

  // Check if this is an AGZ Product
  const isAgzSinglePack = getAgzFlavorsWithGate(normalizedId, store);

  if (isAgzSinglePack) return isAgzSinglePack[0];

  const omegaVariants = SHOPIFY_OMEGA_SUB_BUNDLE_VARIANT_ID[store];
  if (!omegaVariants) return variantId;

  const isOmegaSubProduct =
    normalizedId === omegaVariants.default ||
    normalizedId === omegaVariants.subscriptionPrice ||
    normalizedId === omegaVariants.bundlePrice;

  // Check if this is an Omega discounted variant
  if (isOmegaSubProduct) {
    // If so, return default variant id (the one displayed in Add Products Modal)
    return omegaVariants.default;
  }

  return normalizedId;
};

/**
 * Determines whether to show the `Member preferred flavor` tag. Used for AGZ Chocolate Mint flavor.
 */
export const isPreferredAGZFlavor = (store: StoreCode, variantId?: string): boolean =>
  getAgzVariantsWithOtp(store).singlePack.chocolateMint.some(
    (value) => value === variantId?.replace('gid://shopify/ProductVariant/', '')
  );
